import { useState, useEffect } from "react";
import Modal from "../../../components/ui/modal";
import Dropdown from "../../../components/ui/dropdown";
import colors from "../../../utils/colors";
import DateInput from "../../../components/dateInput";
import Button from "../../../components/ui/button";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { initialStateValue } from "../utils";
import { useFormik } from "formik";
import * as yup from "yup";
import { Text } from "@chakra-ui/react";
import { useCreatePatient } from "../../../apis/patients";
import moment from "moment";

interface IProps {
  isOpen: boolean;
  onClose: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
}

const CreatePatientModal = ({ isOpen, onClose, title }: IProps) => {
  const handleClose = () => {
    onClose(false);
  };

  const { mutate: createPatient, isPending } = useCreatePatient();
  const { _id } = useAppSelector((state) => state.auth.doctorInfo);

  const [ageDisplay, setAgeDisplay] = useState("");

  const formik = useFormik({
    // initialValues: initialStateValue,
    // patientNotes: "", // ✅ NEW
    initialValues: {
      ...initialStateValue,
      patientNotes: "", // ✅ NEW
    },
    validationSchema: yup.object({
      name: yup.string().required("Patient's name is required"),
      email: yup.string().required("Patient's email is required"),
      phoneNumber: yup.string().required("Patient's phone number is required"),
      height: yup
        .number()
        .min(1, "Patient's height is required")
        .required("Patient's height is required"),
      weight: yup
        .number()
        .min(1, "Patient's weigth is required")
        .required("Patient's weight is required"),
      bloodGroup: yup.string().required("Patient's blood group is required"),
      bloodGenotype: yup
        .string()
        .required("Patient's blood genotype is required"),
      gender: yup.string().required("Patient's gender is required"),
      address: yup.string().required("Patient's address is required"),
      dateOfBirth: yup.mixed().required("Patient's date of birth is required"),
      riskCategory: yup
        .string()
        .required("Patient's risk category is required"),
      patientNotes: yup.string().max(10000, "Notes too long").optional(), // ✅ NEW
    }),
    onSubmit: (values) => {
      createPatient(
        {
          ...values,
          doctorId: _id,
          dateRegistered: new Date(),
          dateOfBirth: values.dateOfBirth,
          patientNotes: values.patientNotes || undefined, // ✅ send only if provided
        },
        {
          onSuccess: () => handleClose(),
        }
      );
    },
  });

  useEffect(() => {
    const calculateAge = (dob) => {
      if (!dob) return "";

      const birthDate = moment(dob);
      const now = moment();
      const years = now.diff(birthDate, "years");
      const months = now.diff(birthDate, "months") % 12;

      if (years === 0) {
        return months === 1 ? "1 month" : `${months} months`;
      } else if (years === 1) {
        return months > 0 ? `1 year, ${months} months` : "1 year";
      } else if (years === 2 && months === 0) {
        return "2 years";
      } else if (years < 2) {
        return `${years} years, ${months} months`;
      }

      return `${years} years`;
    };

    setAgeDisplay(calculateAge(formik.values.dateOfBirth));
  }, [formik.values.dateOfBirth]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      className="w-[35%] min-w-[400px] max-w-[500px] p-[40px] h-[80%] max-h-[700px]"
    >
      <div className="flex flex-col gap-4 mt-6">
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's name</p>
          <input
            onChange={(e) => formik.setFieldValue("name", e.target.value)}
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's name`}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.name && formik?.touched?.name && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.name}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's email</p>
          <input
            onChange={(e) => formik.setFieldValue("email", e.target.value)}
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's email`}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.email && formik?.touched?.email && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.email}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's phone number</p>
          <input
            onChange={(e) =>
              formik.setFieldValue("phoneNumber", e.target.value)
            }
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's phone number`}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.phoneNumber && formik?.touched?.phoneNumber && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.phoneNumber}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's height (cm)</p>
          <input
            onChange={(e) =>
              formik.setFieldValue("height", Number(e.target.value))
            }
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's height`}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.height && formik?.touched?.height && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.height}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's weight (KG)</p>
          <input
            onChange={(e) =>
              formik.setFieldValue("weight", Number(e.target.value))
            }
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's weight`}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.weight && formik?.touched?.weight && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.weight}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's address</p>
          <input
            onChange={(e) => formik.setFieldValue("address", e.target.value)}
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder={`Enter patient's address`}
            className="placeholder:text-[#606060] text-[14px] h-[40px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.address && formik?.touched?.address && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.address}
            </Text>
          )}
        </div>
        <div className=" w-full">
          <p className="text-sm mb-2">Patient's Date of birth</p>
          <DateInput
            value={formik?.values?.dateOfBirth}
            handleChange={(e: any) => formik.setFieldValue("dateOfBirth", e)}
            useAllYears={true}
          />
          {formik?.errors?.dateOfBirth && formik?.touched?.dateOfBirth && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {String(formik?.errors?.dateOfBirth)}
            </Text>
          )}
        </div>
        <div className="w-full">
          <p className="text-sm mb-2">Blood group:</p>
          <Dropdown
            onSelect={(s) => formik.setFieldValue("bloodGroup", s)}
            options={["A+", "B+", "AB+", "O+", "A-", "B-", "AB-", "O-"]}
            className="!h-[40px] !w-full"
            placeholder="Select blood group"
            value={formik?.values?.bloodGroup}
          />
          {formik?.errors?.bloodGroup && formik?.touched?.bloodGroup && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.bloodGroup}
            </Text>
          )}
        </div>
        <div className="w-full">
          <p className="text-sm mb-2">Blood genotype:</p>
          <Dropdown
            onSelect={(s) => formik.setFieldValue("bloodGenotype", s)}
            options={["AA", "AC", "AS", "SS", "CC", "SC"]}
            className="!h-[40px] !w-full"
            placeholder="Select genotype"
            value={formik?.values?.bloodGenotype}
          />
          {formik?.errors?.bloodGenotype && formik?.touched?.bloodGenotype && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.bloodGenotype}
            </Text>
          )}
        </div>
        <div className="flex w-full">
          <div className="w-full">
            <p className="text-sm mb-2">Gender:</p>
            <Dropdown
              onSelect={(s) => formik.setFieldValue("gender", s)}
              options={["Male", "Female"]}
              className="!h-[40px] !w-full"
              placeholder="Select gender"
              value={formik?.values?.gender}
            />
            {formik?.errors?.gender && formik?.touched?.gender && (
              <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
                {formik?.errors?.gender}
              </Text>
            )}
          </div>
        </div>
        <div className="flex w-full">
          <div className="w-full">
            <p className="text-sm mb-2">Risk category:</p>
            <Dropdown
              onSelect={(s) => formik.setFieldValue("riskCategory", s)}
              options={["Low Risk", "Medium Risk", "High Risk"]}
              className="!h-[40px] !w-full"
              placeholder="Select risk category"
              value={formik?.values?.riskCategory}
            />
            {formik?.errors?.riskCategory && formik?.touched?.riskCategory && (
              <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
                {formik?.errors?.riskCategory}
              </Text>
            )}
          </div>
        </div>
        {/* ✅ NEW: Patient Notes */}
        <div className="w-full">
          <p className="text-sm mb-2">Patient Notes (optional)</p>
          <textarea
            value={formik.values.patientNotes}
            onChange={(e) =>
              formik.setFieldValue("patientNotes", e.target.value)
            }
            style={{
              border: "1px solid",
              borderColor: colors.grey,
              color: colors.placeholderColor,
            }}
            placeholder="Enter any notes for this patient"
            className="placeholder:text-[#606060] text-[14px] min-h-[100px] rounded-md w-full p-2 outline-none"
          />
          {formik?.errors?.patientNotes && formik?.touched?.patientNotes && (
            <Text fontSize={"13px"} color={colors.red} mt={"2px"}>
              {formik?.errors?.patientNotes}
            </Text>
          )}
        </div>

        <Button
          text="Create patient"
          theme="primary"
          size="md"
          className="rounded-md"
          loading={isPending}
          onClick={() => formik?.handleSubmit()}
          type="submit"
        />
      </div>
    </Modal>
  );
};

export default CreatePatientModal;
